import { ChevronDown } from "lucide-react";
import { DateTime } from "luxon";
import React, { useEffect } from "react";

import { IgButton, IgIconButton, IgTypography } from "@intelligems/ig-theme";
import { Box, Menu, MenuItem } from "@mui/material";
import Grid from "@mui/material/Grid";
import { useGetSitewideAnalyticsChatHistory } from "@src/api/ai/sitewide-analytics/use-get-sitewide-analytics-chat-history";
import { aiSitewideAnalyticsKeys } from "@src/api/queryKeys";
import { SkeletonGroup } from "@src/components/Utils/SkeletonGroup";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useSelector } from "react-redux";
import { getAiChat } from "@src/store/slices/aiChat";

interface SitewideAnalyticsChatHistoryProps {
  currentSessionId: string;
  onSessionIdChange: (id: string) => void;
  orgId: string | undefined | null;
  debugMode?: boolean;
}

export const SitewideAnalyticsChatHistory = ({
  currentSessionId,
  onSessionIdChange,
  orgId,
  debugMode,
}: SitewideAnalyticsChatHistoryProps) => {
  const queryClient = useQueryClient();
  const chatHistoryList = useGetSitewideAnalyticsChatHistory({ orgId });
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  useEffect(() => {
    if (orgId) {
      void queryClient.invalidateQueries({
        queryKey: aiSitewideAnalyticsKeys.chatHistory(orgId),
      });
    }
  }, [orgId, queryClient]);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelectSession = (sessionId: string) => {
    onSessionIdChange(sessionId);
    handleClose();
  };

  if (!chatHistoryList.isSuccess) {
    return (
      <Box width={"150px"}>
        <SkeletonGroup lines={1} />
      </Box>
    );
  }

  const chatSessions = chatHistoryList.data ?? [];

  return (
    <Grid container columnSpacing={1} flexGrow={1} alignItems="center">
      <Grid>
        <IgButton variant={"text"} onClick={() => onSessionIdChange("new")}>
          New Chat {debugMode && "(Debug)"}
        </IgButton>
      </Grid>
      {chatSessions.length > 0 ? (
        <Grid>
          <IgIconButton onClick={handleClick}>
            <ChevronDown size={16} />
          </IgIconButton>
          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "left",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "left",
            }}
          >
            {chatSessions.map((session) => (
              <MenuItem
                onClick={() => handleSelectSession(session.sessionId)}
                key={session.sessionId}
                value={session.sessionId}
                selected={currentSessionId === session.sessionId}
              >
                <Box display="flex" flexDirection="column" gap={0.25}>
                  <Box>
                    <IgTypography variant="body2">
                      {session.title ?? session.firstUserMessage ?? "Chat"}
                    </IgTypography>
                  </Box>
                  <Box>
                    <IgTypography fontSize={"0.75rem"} color="text.secondary">
                      {DateTime.fromMillis(
                        session.lastMessageAt
                      ).toLocaleString(DateTime.DATETIME_SHORT)}
                    </IgTypography>
                  </Box>
                </Box>
              </MenuItem>
            ))}
          </Menu>
        </Grid>
      ) : null}
    </Grid>
  );
};
